import React, { useEffect, useState } from 'react';
import { KeyRound, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '../../lib/api';
import { useT } from '../../context/LanguageContext';
import { registrationOptionsForBrowser, serializePasskeyCredential } from '../../lib/passkeys';

const PasskeyManager = ({ user, onUpdated }) => {
  const { t } = useT();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passkeys, setPasskeys] = useState([]);

  const loadPasskeys = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/auth/passkeys');
      setPasskeys(data.passkeys || []);
      onUpdated({ ...user, has_passkey: Boolean(data.passkeys?.length) });
    } catch (error) {
      toast.error(formatApiError(error, t('Kunne ikkje hente passnøklane.')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    api.get('/auth/passkeys')
      .then(({ data }) => {
        if (!active) return;
        setPasskeys(data.passkeys || []);
      })
      .catch((error) => {
        if (active) toast.error(formatApiError(error, 'Kunne ikkje hente passnøklane.'));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const registerPasskey = async () => {
    if (!window.PublicKeyCredential || !navigator.credentials?.create) {
      toast.error(t('Nettlesaren støttar ikkje passnøklar.'));
      return;
    }

    setSaving(true);
    try {
      const { data: ceremony } = await api.post('/auth/passkeys/register/options');
      const credential = await navigator.credentials.create({
        publicKey: registrationOptionsForBrowser(ceremony.options),
      });
      if (!credential) throw new Error(t('Opprettinga blei avbroten.'));

      await api.post('/auth/passkeys/register/verify', {
        challenge_id: ceremony.challenge_id,
        credential: serializePasskeyCredential(credential),
      });
      await loadPasskeys();
      toast.success(t('Passnøkkelen er lagra på medlemskontoen.'));
    } catch (error) {
      toast.error(formatApiError(error, t('Kunne ikkje registrere passnøkkelen.')));
    } finally {
      setSaving(false);
    }
  };

  const removePasskey = async (passkey) => {
    if (!window.confirm(t('Vil du slette denne passnøkkelen?'))) return;
    try {
      await api.delete(`/auth/passkeys/${encodeURIComponent(passkey.id)}`);
      const remaining = passkeys.filter((item) => item.id !== passkey.id);
      setPasskeys(remaining);
      onUpdated({ ...user, has_passkey: remaining.length > 0 });
      toast.success(t('Passnøkkelen er sletta.'));
    } catch (error) {
      toast.error(formatApiError(error, t('Kunne ikkje slette passnøkkelen.')));
    }
  };

  return (
    <div className="space-y-4" data-testid="passkey-manager">
      {loading ? (
        <p role="status" className="text-sm text-white/50">{t('Lastar passnøklar…')}</p>
      ) : passkeys.length > 0 ? (
        <ul className="divide-y divide-white/10" data-testid="passkey-list">
          {passkeys.map((passkey) => (
            <li key={passkey.id} data-testid="passkey-item" className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
              <KeyRound size={17} className="shrink-0 text-[#ff2e2e]" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">{passkey.name}</p>
                <p className="text-xs text-white/45">{passkey.created_at ? new Date(passkey.created_at).toLocaleDateString() : t('Aktiv')}</p>
              </div>
              <button type="button" data-testid="passkey-delete-button" onClick={() => removePasskey(passkey)} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white/55 transition-colors hover:bg-red-500/10 hover:text-red-300" aria-label={`${t('Slett')} ${passkey.name}`} title={t('Slett passnøkkel')}>
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-white/50">{t('Ingen aktive passnøklar på kontoen.')}</p>
      )}
      <p className="text-white/50 text-sm">
        {user.has_passkey
          ? t('Denne medlemskontoen har ei lagra passnøkkel. Du kan legge til fleire.')
          : t('Legg til ei passnøkkel for å logge inn med e-post eller brukarnamn utan passord.')}
      </p>
      <button type="button" data-testid="passkey-register-button" onClick={registerPasskey} disabled={saving} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-[#ff2e2e] text-[#ff2e2e] font-bold uppercase tracking-wide hover:bg-[#ff2e2e] hover:text-black transition-colors disabled:opacity-60">
        <KeyRound size={16} /> {saving ? t('Registrerer…') : t('Opprett passnøkkel')}
      </button>
    </div>
  );
};

export default PasskeyManager;